import { EM_DASH } from '../Base.constants';
import { CORE_SCORE_RISK_FACTORS } from '../components/widgets/CoreScore/CoreScore.constants';
import { CoreScoresDto } from '../services/dtos/coreScores/coreScores.dto';
import { CoreScoreWidgetProps } from '../models/Score';
import { formatDate } from './dateUtils';

export const CORE = 'Core';
export const STRATEGIC = 'Strategic';
export const MODERATE_RISK = 'Moderate Risk';
export const SPECULATIVE = 'Speculative';

export const CORE_UPPER_LIMIT = 5;
export const CORE_LOWER_LIMIT = 4;
export const STRATEGIC_UPPER_LIMIT = 4;
export const STRATEGIC_LOWER_LIMIT = 3;
export const MODERATE_RISK_UPPER_LIMIT = 3;
export const MODERATE_RISK_LOWER_LIMIT = 2;
export const SPECULATIVE_UPPER_LIMIT = 2;
export const SPECULATIVE_LOWER_LIMIT = 1;
const between = (x: number, min: number, max: number) => {
  return x >= min && x <= max;
};

interface fetchScoresCategoryResponse {
  score: string;
  outlook: string;
}
export const fetchScoresCategory = (scoreNumber: number, outlookNumber: number): fetchScoresCategoryResponse => {
  let score = '';
  let outlook = '';

  if (between(scoreNumber, CORE_LOWER_LIMIT, CORE_UPPER_LIMIT)) {
    score = CORE;
  } else if (between(scoreNumber, STRATEGIC_LOWER_LIMIT, STRATEGIC_UPPER_LIMIT)) {
    score = STRATEGIC;
  } else if (between(scoreNumber, MODERATE_RISK_LOWER_LIMIT, MODERATE_RISK_UPPER_LIMIT)) {
    score = MODERATE_RISK;
  } else if (between(scoreNumber, SPECULATIVE_LOWER_LIMIT, SPECULATIVE_UPPER_LIMIT)) {
    score = SPECULATIVE;
  }

  if (between(outlookNumber, CORE_LOWER_LIMIT, CORE_UPPER_LIMIT)) {
    outlook = CORE;
  } else if (between(outlookNumber, STRATEGIC_LOWER_LIMIT, STRATEGIC_UPPER_LIMIT)) {
    outlook = STRATEGIC;
  } else if (between(outlookNumber, MODERATE_RISK_LOWER_LIMIT, MODERATE_RISK_UPPER_LIMIT)) {
    outlook = MODERATE_RISK;
  } else if (between(outlookNumber, SPECULATIVE_LOWER_LIMIT, SPECULATIVE_UPPER_LIMIT)) {
    outlook = SPECULATIVE;
  }

  return { score, outlook };
};

export const getScoreState = (score: number, scoreType: string): string => {
  const isRiskFactor = CORE_SCORE_RISK_FACTORS.includes(scoreType);
  switch (score) {
    case 5:
      return isRiskFactor ? 'Low' : 'Strong';
    case 4:
      return isRiskFactor ? 'Moderately Low' : 'Above Average';
    case 3:
      return isRiskFactor ? 'Moderate' : 'Average';
    case 2:
      return isRiskFactor ? 'Moderately High' : 'Below Average';
    case 1:
      return isRiskFactor ? 'High' : 'Weak';
    default:
      return EM_DASH;
  }
};

export const setScoreDataFormat = (rawScores: CoreScoresDto | undefined): any => {
  if (!rawScores) {
    return { isScore: false };
  }

  let formattedObject: CoreScoreWidgetProps = { isScore: true };
  if (rawScores.leverageAssessment && rawScores.leverageOutlook && rawScores.cashAssessment && rawScores.cashOutlook) {
    formattedObject = {
      riskFactor: [
        {
          label: 'Sector Risk',
          score: rawScores.sectorAssessment,
          outlook: rawScores.sectorOutlook,
        },
        {
          label: 'Event Risk',
          score: rawScores.eventAssessment,
          outlook: rawScores.eventOutlook,
        },
      ],
      nonRiskFactor: [
        {
          label: 'Leverage',
          score: rawScores.leverageAssessment,
          outlook: rawScores.leverageOutlook,
        },
        {
          label: 'Cash Flow',
          score: rawScores.cashAssessment,
          outlook: rawScores.cashOutlook,
        },
        {
          label: 'Liquidity',
          score: rawScores.liquidityAssessment,
          outlook: rawScores.liquidityOutlook,
        },
        {
          label: 'Profitability',
          score: rawScores.profitabilityAssessment,
          outlook: rawScores.profitabilityOutlook,
        },
      ],
      scoreNumber: parseFloat(rawScores.assessment?.toFixed(2)),
      outlookNumber: parseFloat(rawScores.outlook?.toFixed(2)),
      lastChanged: formatDate(new Date(rawScores.changedAt)),
      lastReviewed: formatDate(new Date(rawScores.reviewedAt)),
      isScore: true,
    };
  } else if (
    rawScores.capitalAssessment &&
    rawScores.capitalOutlook &&
    rawScores.assetQualityAssessment &&
    rawScores.assetQualityOutlook
  ) {
    formattedObject = {
      riskFactor: [
        {
          label: 'Sector Risk',
          score: rawScores.sectorAssessment,
          outlook: rawScores.sectorOutlook,
        },
        {
          label: 'Event Risk',
          score: rawScores.eventAssessment,
          outlook: rawScores.eventOutlook,
        },
      ],
      nonRiskFactor: [
        {
          label: 'Capital',
          score: rawScores.capitalAssessment,
          outlook: rawScores.capitalOutlook,
        },
        {
          label: 'Asset Quality',
          score: rawScores.assetQualityAssessment,
          outlook: rawScores.assetQualityOutlook,
        },
        {
          label: 'Liquidity',
          score: rawScores.liquidityAssessment,
          outlook: rawScores.liquidityOutlook,
        },
        {
          label: 'Profitability',
          score: rawScores.profitabilityAssessment,
          outlook: rawScores.profitabilityOutlook,
        },
      ],
      scoreNumber: parseFloat(rawScores.assessment?.toFixed(2)),
      outlookNumber: parseFloat(rawScores.outlook?.toFixed(2)),
      lastChanged: formatDate(new Date(rawScores.changedAt)),
      lastReviewed: formatDate(new Date(rawScores.reviewedAt)),
      isScore: true,
    };
  }

  return formattedObject;
};

export const hideCoreScoreView = (coreScores: CoreScoresDto | undefined): boolean => !coreScores;
